import { STT_PROVIDER_LANGUAGE_MAP } from '@/constants/voiceProviderLanguageMap';
import { STTModelInfo } from '@/shared/types/voiceProviders';

interface FormHelpers {
  setValue: (path: string, value: unknown, options?: { shouldDirty: boolean }) => void;
  watch: (path: string) => unknown;
}

/**
 * Syncs Deepgram language and model after a language change.
 *
 * When the user picks a new language, the current Deepgram model may not
 * support it. This helper falls back to 'en' for the language and
 * auto-switches to a compatible model when necessary.
 */
export const syncDeepgramModel = (
  languageCode: string,
  sttModels: STTModelInfo[],
  { setValue, watch }: FormHelpers
) => {
  if (sttModels.length === 0) {
    return;
  }

  const deepgramCode = STT_PROVIDER_LANGUAGE_MAP.deepgram.toProvider(languageCode) as string;
  const deepgramModels = sttModels.filter((m) => m.provider === 'deepgram');
  const isLanguageSupportedByDeepgram = deepgramModels.some((m) =>
    m.supportedLanguages.includes(deepgramCode)
  );

  if (!isLanguageSupportedByDeepgram) {
    setValue('config.stt_deepgram.language', 'en', { shouldDirty: true });
  }

  const deepgramLanguage = isLanguageSupportedByDeepgram ? deepgramCode : 'en';
  const currentDeepgramModel = watch('config.stt_deepgram.model') as string | undefined;
  const currentModelInfo = sttModels.find((m) => m.id === currentDeepgramModel);

  if (currentModelInfo && !currentModelInfo.supportedLanguages.includes(deepgramLanguage)) {
    const compatibleModel = deepgramModels.find((m) =>
      m.supportedLanguages.includes(deepgramLanguage)
    );

    if (compatibleModel) {
      setValue('config.stt_deepgram.model', compatibleModel.id, { shouldDirty: true });
      // setLanguage forced 'en' for the old en-only model; rewrite now that a
      // model supporting the picked language is selected.
      setValue('config.stt_deepgram.language', deepgramLanguage, { shouldDirty: true });
    }
  }
};
